import React from 'react';
import { Typography } from '@mui/material';
import Spacing from 'components/Spacing';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import FieldRow from 'components/FormElements/FieldRow';
import api from 'lib/trpcClient';
import ButtonGroup from 'components/ButtonGroup';
import PageSection from 'components/PageSection';
import Button from 'components/Button';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CalendarDay, pluralize } from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHistory } from 'react-router-dom';
import {
  createEqualisationEditorRoute,
  EQUALISATIONS_PROCESS_CLOSE_SUCCESS,
} from 'adminConstants/routes';
import EqualisationFooter from '../EqualisationFooter';
import { useEqualisationSection } from '../EqualisationContext';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import { useDisclosure } from 'further-ui/hooks';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import { getQueryKey } from '@trpc/react-query';
import CommitmentsTable from './CommitmentsTable';
import { AddCommitmentsToEqualisationFormValues } from './FormWrapper';
import { Controller, useFormContext } from 'react-hook-form';
import { AppTextInput } from 'components/FormElements';

interface AddCommitmentsToEqualisationFormProps {
  savedEqualisationId?: string;
}

const AddCommitmentsToEqualisationForm: React.FC<
  AddCommitmentsToEqualisationFormProps
> = ({ savedEqualisationId }) => {
  const { vehicle } = useLayoutContext();

  const { goToNextSection, nextSection } = useEqualisationSection();

  const { hasEqualisationInDraftState } = useEqualisations(vehicle?._id ?? '');
  const confirmDialog = useDisclosure();

  const [equalisationsData] = api.equalisations.list.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
  });

  const earliestDateForClose = equalisationsData?.earliestDateForClose;
  const history = useHistory();

  const listEqualisationsQueryKey = getQueryKey(api.equalisations.list, {
    vehicleId: vehicle?._id ?? '',
  });

  const getVehiclesQueryKey = getQueryKey(api.vehicle.listAllVehicles, {
    order: 'desc',
    orderBy: 'vehicleName',
  });

  const commitmentsQueryKey = getQueryKey(api.commitments.list, {
    vehicleId: vehicle?._id ?? '',
    onlyEqualised: true,
  });

  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getEqualisationActionAllocations,
    {
      equalisationSection: nextSection,
      vehicleId: vehicle?._id ?? '',
      equalisationId: savedEqualisationId ?? '',
    },
  );

  const equalisationQueryKey = getQueryKey(api.equalisations.get, {
    equalisationId: savedEqualisationId ?? '',
    vehicleId: vehicle?._id ?? '',
  });

  const createEqualisation = api.equalisations.create.useMutation(
    useHandleTrpcMutation<{ id?: string }>()({
      onSuccess: (data) => {
        if (equalisationsData.results.length) {
          history.push(createEqualisationEditorRoute(data?.id ?? ''));
        } else {
          history.push(EQUALISATIONS_PROCESS_CLOSE_SUCCESS);
        }
      },
      invalidations: [
        listEqualisationsQueryKey,
        getVehiclesQueryKey,
        commitmentsQueryKey,
      ],
    }),
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation<{ success: boolean }>()({
      onSuccess: () => {
        goToNextSection?.();
      },
      invalidations: [
        equalisationActionAllocationsQueryKey,
        equalisationQueryKey,
        commitmentsQueryKey,
      ],
    }),
  );

  const { handleSubmit, control, watch, formState } =
    useFormContext<AddCommitmentsToEqualisationFormValues>();

  const unEqualisedFormCommitments =
    watch('commitments')?.filter((c) => !c.equalisationDate) ?? [];

  const onSubmit = (data: AddCommitmentsToEqualisationFormValues) => {
    if (savedEqualisationId && nextSection) {
      updateEqualisation.mutate({
        equalisationId: savedEqualisationId,
        vehicleId: vehicle?._id ?? '',
        currentSection: nextSection,
        commitments: data.commitments,
        closeDate: new CalendarDay(data.closeDate),
        name: data.name,
      });
    } else {
      const selectedCommitments = data.commitments.filter((c) => c.isSelected);
      createEqualisation.mutate({
        vehicleId: vehicle?._id ?? '',
        closeDate: new CalendarDay(data.closeDate),
        name: data.name,
        commitments: selectedCommitments,
      });
    }
  };

  if (unEqualisedFormCommitments.length === 0) {
    return (
      <Spacing>
        <PageSection title="Add new fund close" withSpacing>
          <Spacing size="sm">
            <Typography variant="body1" color="text.secondary">
              There are no commitments pending close.
            </Typography>
          </Spacing>
        </PageSection>
      </Spacing>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Spacing>
        <PageSection title="Add new fund close" withSpacing>
          <Spacing size="sm">
            <Typography variant="body1" color="text.secondary">
              You have {unEqualisedFormCommitments.length}{' '}
              {pluralize('commitment', unEqualisedFormCommitments.length)}{' '}
              pending close.
            </Typography>
            <Typography variant="body1" color="text.secondary">
              If this is a second or subsequent close, check that all vehicle
              actions from before this close date have been entered into the
              system before continuing.
            </Typography>
          </Spacing>
          <FieldRow title="Close date" centerTitle>
            <Controller
              name="closeDate"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <AppDatePicker
                  id="close-date"
                  value={value ?? null}
                  onChange={onChange}
                  required
                  placeholder="Select date"
                  inputVariant="outlined"
                  size="small"
                  minDate={earliestDateForClose?.toLocalDate()}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FieldRow>
          <FieldRow title="Close name" centerTitle>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppTextInput
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FieldRow>
        </PageSection>
        <CommitmentsTable />
        <PageSection withSpacing>
          {savedEqualisationId ? (
            <EqualisationFooter
              handleNext={handleSubmit(onSubmit)}
              handlePrevious={() => {}}
              disabled={!formState.isValid || formState.isSubmitting}
              loading={updateEqualisation.isPending}
            />
          ) : (
            <ButtonGroup>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                onClick={
                  hasEqualisationInDraftState
                    ? (e) => {
                        e.preventDefault();
                        confirmDialog.onOpen();
                      }
                    : undefined
                }
                loading={createEqualisation.isPending}
                disabled={!formState.isValid || formState.isSubmitting}
              >
                Process close
              </Button>
              {hasEqualisationInDraftState && (
                <PendingDraftEqualisationsModal
                  isOpen={confirmDialog.isOpen}
                  onClose={confirmDialog.onClose}
                  onConfirm={handleSubmit(onSubmit)}
                  loading={createEqualisation.isPending}
                />
              )}
            </ButtonGroup>
          )}
        </PageSection>
      </Spacing>
    </form>
  );
};

export default AddCommitmentsToEqualisationForm;
